import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { proFetch } from '../../utils/proApi';
import { Page, EnTetePage } from '../../components/pro/ui';

const SKILLS = [
  'peinture', 'enduit', 'placo', 'carrelage', 'pose PVC', 'parquet',
  'ragréage', 'ponçage', 'ratissage', 'plomberie', 'sanitaire',
  'électricité', 'menuiserie', 'pose porte', 'pose fenêtre',
  'maçonnerie', 'démolition', 'isolation', 'façade', 'chauffage',
  'climatisation', 'toiture', 'échafaudage', 'nettoyage',
];

const STATUT_LABELS = {
  actif: { label: 'Actif', color: 'bg-green-100 text-green-700' },
  inactif: { label: 'Inactif', color: 'bg-slate-100 text-slate-500' },
  conge: { label: 'En congé', color: 'bg-amber-100 text-amber-700' },
};

const PLANNING_TYPES = {
  chantier:  { label: 'Chantier',   color: 'bg-orange-500', text: 'text-white', letter: 'C' },
  conge:     { label: 'Congé',      color: 'bg-slate-300',  text: 'text-slate-700', letter: 'K' },
  indispo:   { label: 'Indisponible', color: 'bg-red-400', text: 'text-white', letter: 'I' },
  formation: { label: 'Formation',  color: 'bg-blue-400',  text: 'text-white', letter: 'F' },
};

function getDaysInMonth(year, month) {
  const days = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    days.push(d.toISOString().split('T')[0]);
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function getMonthLabel(year, month) {
  return new Date(year, month, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

function isDateBetween(date, from, to) {
  if (!from || !to) return false;
  const [a, b] = from <= to ? [from, to] : [to, from];
  return date >= a && date <= b;
}

export default function ProEquipe() {
  const [workers, setWorkers] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showAdd, setShowAdd] = useState(false);

  // Calendar
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  // Range selection: { workerId, date1, date2 }
  const [selection, setSelection] = useState(null);
  const [hoverDate, setHoverDate] = useState(null); // date hovered during range selection
  // Popup
  const [popup, setPopup] = useState(null); // { workerId, from, to }
  const [popupType, setPopupType] = useState('chantier');
  const [popupProject, setPopupProject] = useState('');
  const [popupLabel, setPopupLabel] = useState('');

  function emptyForm() {
    return { prenom: '', nom: '', telephone: '', email: '', skills: [], coutHoraire: 0, statut: 'actif', notes: '' };
  }

  // Fetch data
  useEffect(() => {
    Promise.all([
      proFetch('/workers').then(r => r.json()),
      proFetch('/leads?limit=100').then(r => r.json()),
    ]).then(([w, l]) => {
      setWorkers(w);
      setLeads(l);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchPlanning = useCallback(() => {
    const ds = getDaysInMonth(calYear, calMonth);
    proFetch(`/planning?from=${ds[0]}&to=${ds[ds.length - 1]}`)
      .then(r => r.json()).then(setPlanning).catch(() => setPlanning([]));
  }, [calYear, calMonth]);

  useEffect(() => { fetchPlanning(); }, [fetchPlanning]);

  // Escape key or click outside calendar cancels selection
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') cancelPopup(); };
    const handleClick = (e) => {
      // If clicking outside any calendar cell (td/button inside the table), cancel
      if (selection && !popup && !e.target.closest('.cal-grid')) cancelPopup();
    };
    window.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => { window.removeEventListener('keydown', handleKey); document.removeEventListener('mousedown', handleClick); };
  }); // intentionally no deps

  const days = useMemo(() => getDaysInMonth(calYear, calMonth), [calYear, calMonth]);

  const planMap = useMemo(() => {
    const m = {};
    for (const p of planning) {
      const wId = p.workerId;
      if (!m[wId]) m[wId] = {};
      m[wId][p.date] = p;
    }
    return m;
  }, [planning]);

  const prevMonth = () => { if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); } else setCalMonth(m => m - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); } else setCalMonth(m => m + 1); };

  // Handle cell click — range selection logic
  const lastClickRef = React.useRef({ time: 0, workerId: null, date: null });

  const handleCellClick = (workerId, date) => {
    const now = Date.now();
    const last = lastClickRef.current;

    // Double-click detection (< 400ms, same cell)
    if (now - last.time < 400 && last.workerId === workerId && last.date === date) {
      lastClickRef.current = { time: 0, workerId: null, date: null };
      // Open popup in manual date mode
      setSelection({ workerId, date1: date, date2: date });
      setPopup({ workerId, from: date, to: date, manualDates: true });
      setPopupType('conge');
      setPopupProject('');
      setPopupLabel('');
      setHoverDate(null);
      return;
    }
    lastClickRef.current = { time: now, workerId, date };

    if (!selection || selection.workerId !== workerId) {
      // Start new selection
      setSelection({ workerId, date1: date, date2: null });
    } else if (!selection.date2) {
      // Set second date
      const from = selection.date1 <= date ? selection.date1 : date;
      const to = selection.date1 <= date ? date : selection.date1;
      setSelection({ ...selection, date2: date });
      // Open popup
      setPopup({ workerId, from, to });
      setPopupType('chantier');
      setPopupProject('');
      setPopupLabel('');
    } else {
      // Reset and start new
      setSelection({ workerId, date1: date, date2: null });
      setPopup(null);
    }
  };

  // Confirm popup — bulk set planning
  const confirmPopup = async () => {
    if (!popup) return;
    const entries = [];
    const d = new Date(popup.from);
    const end = new Date(popup.to);
    const label = popupType === 'chantier'
      ? (leads.find(l => l.ref === popupProject)?.client?.prenom + ' ' + leads.find(l => l.ref === popupProject)?.client?.nom || popupProject).trim()
      : popupLabel;

    while (d <= end) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) { // skip weekends
        entries.push({
          workerId: popup.workerId,
          date: d.toISOString().split('T')[0],
          type: popupType,
          projectRef: popupType === 'chantier' ? popupProject : '',
          label: label || '',
          heures: 8,
        });
      }
      d.setDate(d.getDate() + 1);
    }

    if (entries.length > 0) {
      await proFetch('/planning/bulk', { method: 'POST', body: JSON.stringify({ entries }) });
      fetchPlanning();
    }

    setPopup(null);
    setSelection(null);
    setHoverDate(null);
  };

  // Cancel / clear selection
  const cancelPopup = () => { setPopup(null); setSelection(null); setHoverDate(null); };

  // Delete range
  const deleteRange = async () => {
    if (!popup) return;
    const d = new Date(popup.from);
    const end = new Date(popup.to);
    while (d <= end) {
      const date = d.toISOString().split('T')[0];
      await proFetch(`/planning/${popup.workerId}/${date}`, { method: 'DELETE' }).catch(() => {});
      d.setDate(d.getDate() + 1);
    }
    fetchPlanning();
    setPopup(null);
    setSelection(null);
    setHoverDate(null);
  };

  // Worker CRUD
  const save = async () => {
    try {
      if (editingId) {
        const res = await proFetch(`/workers/${editingId}`, { method: 'PATCH', body: JSON.stringify(form) });
        setWorkers(prev => prev.map(w => w._id === editingId ? (res.ok ? { ...w, ...form } : w) : w));
      } else {
        const res = await proFetch('/workers', { method: 'POST', body: JSON.stringify(form) });
        const created = await res.json();
        setWorkers(prev => [...prev, created]);
      }
      setEditingId(null); setShowAdd(false); setForm(emptyForm());
    } catch (err) { alert('Erreur : ' + err.message); }
  };

  const startEdit = (w) => {
    setEditingId(w._id);
    setForm({ prenom: w.prenom, nom: w.nom, telephone: w.telephone || '', email: w.email || '', skills: w.skills || w.specialites || [], coutHoraire: w.coutHoraire || 0, statut: w.statut || 'actif', notes: w.notes || '' });
    setShowAdd(true); setView('list');
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet ouvrier ?')) return;
    await proFetch(`/workers/${id}`, { method: 'DELETE' });
    setWorkers(prev => prev.filter(w => w._id !== id));
  };

  const toggleSkill = (skill) => setForm(prev => ({
    ...prev,
    skills: prev.skills.includes(skill) ? prev.skills.filter(s => s !== skill) : [...prev.skills, skill],
  }));

  const activeWorkers = workers.filter(w => w.statut === 'actif');
  const today = new Date().toISOString().split('T')[0];

  // Active leads for project picker (accepted or in progress)
  const activeLeads = leads.filter(l => ['nouveau', 'visite', 'devis_envoye', 'accepte', 'en_travaux'].includes(l.status || 'nouveau'));

  if (loading) return <div className="text-center py-20 text-slate-400">Chargement...</div>;

  return (
    <Page large>
      <EnTetePage titre="Équipe" sous={`${activeWorkers.length} actif${activeWorkers.length > 1 ? 's' : ''} sur ${workers.length}`}>
        <>
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setView('calendar')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'calendar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Calendrier</button>
            <button onClick={() => setView('list')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Fiches</button>
          </div>
          <button onClick={() => { setShowAdd(!showAdd); setEditingId(null); setForm(emptyForm()); setView('list'); }} className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-4 py-2.5 rounded-xl text-sm">
            + Ouvrier
          </button>
        </>
      </EnTetePage>

      {view === 'calendar' ? (
        <div>
          {/* Month nav */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold">&larr;</button>
            <h2 className="text-lg font-bold text-slate-900 capitalize">{getMonthLabel(calYear, calMonth)}</h2>
            <button onClick={nextMonth} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold">&rarr;</button>
          </div>

          {/* Legend + hint */}
          <div className="flex gap-3 mb-3 flex-wrap">
            {Object.entries(PLANNING_TYPES).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className={`w-3 h-3 rounded ${v.color}`} /> {v.label}
              </span>
            ))}
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-3 h-3 rounded bg-white border border-slate-200" /> Dispo
            </span>
            <span className="text-xs text-slate-400 ml-auto">
              {selection && !selection.date2
                ? `Sélectionnez la date de fin pour ${workers.find(w => w._id === selection.workerId)?.prenom || '?'}`
                : 'Cliquez sur une date pour commencer la sélection'
              }
            </span>
          </div>

          {/* Grid */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" onClick={(e) => { if (e.target === e.currentTarget && selection && !popup) cancelPopup(); }}>
            <table className="cal-grid w-full text-xs" style={{ minWidth: days.length * 32 + 150 }}>
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="sticky left-0 bg-white z-10 text-left px-3 py-2 font-semibold text-slate-600 w-36">Ouvrier</th>
                  {days.map(d => {
                    const dayNum = parseInt(d.split('-')[2]);
                    const dow = new Date(d).getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    const isToday = d === today;
                    return (
                      <th key={d} className={`px-0 py-2 font-semibold text-center w-8 ${isToday ? 'text-orange-600' : isWeekend ? 'text-slate-300' : 'text-slate-500'}`}>
                        <div>{['D','L','M','M','J','V','S'][dow]}</div>
                        <div className={isToday ? 'bg-orange-600 text-white rounded-full w-5 h-5 flex items-center justify-center mx-auto' : ''}>{dayNum}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {activeWorkers.map(w => (
                  <tr key={w._id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="sticky left-0 bg-white z-10 px-3 py-1.5">
                      <button onClick={() => startEdit(w)} className="text-left hover:text-orange-600">
                        <div className="font-semibold text-slate-800">{w.prenom} {w.nom?.[0]}.</div>
                        <div className="text-[10px] text-slate-400">{(w.skills || []).slice(0, 3).join(', ')}</div>
                      </button>
                    </td>
                    {days.map(d => {
                      const dow = new Date(d).getDay();
                      const isWeekend = dow === 0 || dow === 6;
                      const entry = planMap[w._id]?.[d];
                      const pt = entry ? PLANNING_TYPES[entry.type] : null;

                      // Range selection highlighting
                      const isSameWorker = selection?.workerId === w._id;
                      const selecting = isSameWorker && selection.date1 && !selection.date2;
                      const isConfirmed = isSameWorker && selection.date2 && isDateBetween(d, selection.date1, selection.date2);
                      const isHoverPreview = selecting && hoverDate && hoverDate !== selection.date1 && !isWeekend && isDateBetween(d, selection.date1, hoverDate);
                      const isAnchor = selecting && selection.date1 === d;

                      return (
                        <td key={d} className="px-0 py-1 text-center"
                          onMouseEnter={() => { if (selecting) setHoverDate(d); }}
                        >
                          <button
                            onClick={() => handleCellClick(w._id, d)}
                            style={isHoverPreview && !pt ? { backgroundColor: 'rgba(147, 197, 253, 0.5)' } : undefined}
                            className={`w-7 h-7 rounded transition-all ${
                              pt ? `${pt.color} ${pt.text}` :
                              isConfirmed ? 'bg-blue-400 text-white' :
                              isAnchor ? 'bg-blue-500 text-white ring-2 ring-blue-300 ring-offset-1' :
                              isHoverPreview ? 'border border-blue-400' :
                              isWeekend ? 'bg-slate-50 text-slate-300' :
                              'bg-white border border-slate-100 hover:border-blue-300 hover:bg-blue-50'
                            }`}
                            title={entry ? `${pt?.label}${entry.label ? ': ' + entry.label : ''}${entry.projectRef ? ' (' + entry.projectRef + ')' : ''}` : d}
                          >
                            {pt ? pt.letter : ''}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── POPUP: assign range ── */}
          {popup && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={cancelPopup}>
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
                <h3 className="font-black text-lg text-slate-900 mb-1">
                  Planifier {workers.find(w => w._id === popup.workerId)?.prenom}
                </h3>
                {popup.manualDates ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Du</label>
                      <input type="date" value={popup.from}
                        onChange={e => setPopup(p => ({ ...p, from: e.target.value }))}
                        className="w-full px-3 py-3 border-2 border-slate-200 rounded-xl text-sm font-bold focus:border-orange-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Au</label>
                      <input type="date" value={popup.to}
                        onChange={e => setPopup(p => ({ ...p, to: e.target.value }))}
                        className="w-full px-3 py-3 border-2 border-slate-200 rounded-xl text-sm font-bold focus:border-orange-500 outline-none" />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mb-5">
                    Du <strong>{new Date(popup.from).toLocaleDateString('fr-FR')}</strong> au <strong>{new Date(popup.to).toLocaleDateString('fr-FR')}</strong>
                  </p>
                )}

                {/* Type selector */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
                  {Object.entries(PLANNING_TYPES).map(([k, v]) => (
                    <button
                      key={k}
                      onClick={() => setPopupType(k)}
                      className={`py-3 rounded-xl text-sm font-bold transition-colors ${
                        popupType === k
                          ? `${v.color} ${v.text} ring-2 ring-offset-2 ring-current`
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>

                {/* Project picker (only for chantier) */}
                {popupType === 'chantier' && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Chantier</label>
                    <select
                      value={popupProject}
                      onChange={e => setPopupProject(e.target.value)}
                      className="w-full px-3 py-3 border-2 border-slate-200 rounded-xl text-sm focus:border-orange-500 outline-none bg-white"
                    >
                      <option value="">Sélectionner un chantier...</option>
                      {activeLeads.map(l => (
                        <option key={l.ref} value={l.ref}>
                          {l.ref} — {[l.client?.prenom, l.client?.nom].filter(Boolean).join(' ') || 'Anonyme'}
                          {l.context?.total ? ` (${l.context.total})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Label (for non-chantier types) */}
                {popupType !== 'chantier' && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Motif</label>
                    <input
                      value={popupLabel}
                      onChange={e => setPopupLabel(e.target.value)}
                      placeholder={popupType === 'conge' ? 'Congés payés' : popupType === 'formation' ? 'Formation sécurité' : 'Raison...'}
                      className="w-full px-3 py-3 border-2 border-slate-200 rounded-xl text-sm focus:border-orange-500 outline-none"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={confirmPopup}
                    disabled={popupType === 'chantier' && !popupProject}
                    className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 text-white font-bold rounded-xl text-sm transition-colors"
                  >
                    Confirmer
                  </button>
                  <button onClick={deleteRange} className="px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-xl text-sm">
                    Libérer
                  </button>
                  <button onClick={cancelPopup} className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl text-sm">
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── LIST VIEW ── */
        <div>
          {showAdd && (
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm mb-6">
              <h2 className="font-bold text-slate-900 mb-4">{editingId ? 'Modifier' : 'Nouveau ouvrier'}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Prénom *</label>
                  <input value={form.prenom} onChange={e => setForm(p => ({ ...p, prenom: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Nom *</label>
                  <input value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Téléphone</label>
                  <input type="tel" value={form.telephone} onChange={e => setForm(p => ({ ...p, telephone: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Coût horaire (€/h)</label>
                  <input type="number" step="0.5" value={form.coutHoraire || ''} onChange={e => setForm(p => ({ ...p, coutHoraire: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-500 mb-2">Skills</label>
                <div className="flex flex-wrap gap-2">
                  {SKILLS.map(skill => (
                    <button key={skill} type="button" onClick={() => toggleSkill(skill)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${form.skills.includes(skill) ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                      {skill}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Statut</label>
                  <select value={form.statut} onChange={e => setForm(p => ({ ...p, statut: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm bg-white outline-none">
                    <option value="actif">Actif</option><option value="inactif">Inactif</option><option value="conge">En congé</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Notes</label>
                  <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Certifications, véhicule..." className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm outline-none" />
                </div>
              </div>
              <button onClick={save} disabled={!form.prenom || !form.nom} className="bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 text-white font-bold px-6 py-2.5 rounded-xl text-sm">
                {editingId ? 'Enregistrer' : 'Ajouter'}
              </button>
            </div>
          )}

          {workers.length === 0 ? (
            <div className="text-center py-20 text-slate-400">Aucun ouvrier enregistré</div>
          ) : (
            <div className="grid gap-3">
              {workers.map(w => {
                const st = STATUT_LABELS[w.statut] || STATUT_LABELS.actif;
                return (
                  <div key={w._id} className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shrink-0 ${w.statut === 'actif' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {(w.prenom?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900">{w.prenom} {w.nom}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.color}`}>{st.label}</span>
                        {w.coutHoraire > 0 && <span className="text-xs text-slate-400">{w.coutHoraire} €/h</span>}
                      </div>
                      {w.telephone && <div className="text-sm text-slate-500 mt-0.5">{w.telephone}</div>}
                      {(w.skills || w.specialites)?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(w.skills || w.specialites).map(s => <span key={s} className="px-2 py-0.5 bg-slate-100 rounded text-xs font-semibold text-slate-600">{s}</span>)}
                        </div>
                      )}
                      {w.notes && <div className="text-xs text-slate-500 mt-1 italic">{w.notes}</div>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => startEdit(w)} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-semibold text-slate-600">Modifier</button>
                      <button onClick={() => remove(w._id)} className="px-3 py-1.5 bg-red-50 hover:bg-red-100 rounded-lg text-xs font-semibold text-red-500">Supprimer</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
